/**
 * Render a figure for somewhere narrow — the mobile drawer's three balance columns, where each
 * cell gets roughly a third of the screen.
 *
 * Below the threshold nothing changes: `1,234.56`, grouped, two decimals. Above it the figure
 * switches to a compact unit with a single decimal, because points have no ceiling and a player
 * with 454,052.25 of them was getting `454,0…` truncated mid-number, which reads as a broken
 * layout rather than a large balance.
 *
 *   521.77      -> "521.77"
 *   9999.5      -> "9,999.50"
 *   10000       -> "10K"
 *   454052.25   -> "454K"
 *   2149000     -> "2.1M"
 *
 * Two deliberate choices:
 *
 * - **Truncated, never rounded up.** These are balances. Rounding 2,099,999 up to "2.1M" quotes a
 *   player more than they hold; truncating to "2.0M" only ever understates, which is the safe
 *   direction for money.
 * - **A trailing `.0` is dropped**, so a round ten thousand reads "10K" rather than "10.0K".
 *
 * The exact value is never only here — callers keep it in `title` and `aria-label`, since on a
 * phone there is no hover panel to fall back to.
 */

/** Below this, the plain grouped form still fits, so leave it alone. */
const COMPACT_FROM = 10_000

const UNITS = [
    { limit: 1e12, suffix: 'T' },
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' },
]

/** The full, unabbreviated figure. Use for `title` and `aria-label`. */
export const formatAmountExact = (value: number): string =>
    (Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

export const formatAmount = (value: number): string => {
    if (!Number.isFinite(value)) return formatAmountExact(0)

    const magnitude = Math.abs(value)
    if (magnitude < COMPACT_FROM) return formatAmountExact(value)

    const unit = UNITS.find((u) => magnitude >= u.limit)
    if (!unit) return formatAmountExact(value)

    // `Math.trunc` toward zero rather than `toFixed`, which rounds half away from zero.
    const scaled = Math.trunc((value / unit.limit) * 10) / 10

    return `${scaled.toFixed(1).replace(/\.0$/, '')}${unit.suffix}`
}
